/**
 * Authentication utility functions for handling environment-specific URLs
 */

import { isNativePlatform, NATIVE_URL_SCHEME } from '@lib/platform';
import { logger } from '@utils/logger';
import { isSafeInAppPath } from '@utils/safePath';
import { AUTH_DOOR_PATH, AUTH_LINK_PATH, normalizeRoutePath } from '@lib/magicLink/authLinkParams';
import { AUTH_FLOW_TTL_MS } from '@utils/authFlowTtl';
import { defineSessionSlot, isTrue } from '@utils/sessionSlot';
import {
  MAGIC_LINK_MIRROR_KEYS,
  clearMagicLinkMirror,
  readMagicLinkMirror,
  writeMagicLinkPayloadMirror,
} from '@utils/magicLinkMirror';

/**
 * Gets the base URL for the current environment.
 * On native platforms (Capacitor), uses an explicit env var or falls back to
 * window.location.origin (capacitor://localhost on iOS, https://localhost on Android).
 * Note: OAuth redirect URIs use the deep link scheme via getAuthCallbackUrl(), not this.
 * @returns The base URL for the current environment
 */
export const getBaseUrl = (): string => {
  // Explicit override via env var (useful for native builds or staging)
  const envBase = (import.meta as unknown as { env?: { VITE_AUTH_BASE_URL?: unknown } }).env
    ?.VITE_AUTH_BASE_URL;
  if (typeof envBase === 'string' && envBase.length > 0) {
    return envBase;
  }

  // Returns the current origin (protocol + host) for both web and native.
  // On web: e.g. https://meetball.fun
  // On native: capacitor://localhost (iOS) or https://localhost (Android)
  // Note: OAuth redirect URIs use the deep link scheme (fun.meetball.app://)
  // via getAuthCallbackUrl() and getPostLogoutRedirectUri(), NOT this value.
  const { location } = globalThis;
  return `${location.protocol}//${location.host}`;
};

/**
 * Gets the callback URL for authentication based on environment.
 * On native, uses the deep link scheme so the system browser redirects back to the app.
 * @returns The callback URL for authentication
 */
export const getAuthCallbackUrl = (): string => {
  const envUrl = (import.meta as unknown as { env?: { VITE_AUTH_CALLBACK_URL?: unknown } }).env
    ?.VITE_AUTH_CALLBACK_URL;
  if (typeof envUrl === 'string' && envUrl.length > 0) {
    return envUrl;
  }

  // Native: system browser cannot redirect to capacitor://localhost.
  // Use the app's custom URL scheme registered in Info.plist / AndroidManifest.xml.
  if (isNativePlatform()) {
    return `${NATIVE_URL_SCHEME}://callback`;
  }

  return `${getBaseUrl()}/callback`;
};

/**
 * Gets the post-logout redirect URL based on current origin
 * @returns The URL to redirect to after logout
 */
export const getLogoutRedirectUrl = (): string => {
  return getBaseUrl();
};

/**
 * Gets the post-logout redirect URI appropriate for the current platform (AR-6 fix).
 * On native, signOut needs a deep link URI so the system browser redirects back to the app.
 * On web, uses the current origin.
 */
export const getPostLogoutRedirectUri = (): string => {
  if (isNativePlatform()) {
    return `${NATIVE_URL_SCHEME}://`;
  }
  return getBaseUrl();
};

/**
 * Detects whether an error thrown by a Capacitor/Logto auth flow represents
 * the user intentionally dismissing the system browser (not a real error).
 *
 * Different platforms and SDK versions use different wording, so we check
 * all known variants.
 */
export const isUserCancellation = (error: unknown): boolean => {
  const msg = error instanceof Error ? error.message.toLowerCase() : '';
  return msg.includes('cancelled') || msg.includes('canceled') || msg.includes('user_cancelled');
};

// Session storage keys for preserving intended post-auth navigation
const REDIRECT_PATH_KEY = 'meetball_redirect_path';
const REDIRECT_TIMESTAMP_KEY = 'meetball_redirect_timestamp';

/**
 * Re-exported from the module that owns it, next to the capture it governs.
 * See the docblock on `normalizeRoutePath` in
 * src/lib/magicLink/authLinkParams.ts for why the rule lives there and not here.
 */
export { normalizeRoutePath };

/**
 * The genuinely auth-flow routes, i.e. the ones that must never be stored as a
 * post-auth destination and must never trigger the protected-route auto-guard.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: /auth and /auth/link belong here. A
 * signed-out visitor landing cold on the door (or on a spent magic-link URL)
 * must not have THAT path stashed as the post-auth redirect, and the auto-guard
 * must not fire before AuthDoor ever mounts -- either way the user ends up back
 * on the door instead of wherever they meant to go.
 *
 * Deliberately does NOT include '/hi', '/home' or '/': those are
 * `shouldStoreRedirectPath`'s own extra exclusions (guest-friendly landing
 * pages), not auth routes, and folding them in here would make `isPublicRoute`
 * and the auto-guard disagree with reality.
 *
 * EXPORTED because it is also the source of truth for three things that live
 * outside this module graph and cannot import a predicate: `router.tsx`'s route
 * table (which consumes the constants it is built from), the inline referrer
 * gate at the top of `index.html`'s `<head>`, and the `Referrer-Policy` blocks
 * in `public/_headers`. `src/lib/magicLink/authReferrerGate.test.ts` pins all
 * three against this set, which is what makes "add a route here and the edge
 * rules follow" true rather than a line in a checklist. Six of the seven places
 * this set is restated fail QUIETLY when they drift -- a missed router entry is
 * a loud 404, a missed header rule is a bearer token in an edge access log.
 */
export const AUTH_ROUTE_PATHS: ReadonlySet<string> = new Set([
  '/signup',
  '/callback',
  '/logout',
  AUTH_DOOR_PATH,
  AUTH_LINK_PATH,
]);

/**
 * The subset of the above that carries a SECRET on the query string, and the
 * reason the referrer rules exist at all: `/auth/link` a one-time bearer token,
 * `/auth` an email address, `/callback` the OIDC `code` and `state`. `/signup`
 * and `/logout` carry neither.
 */
export const SECRET_BEARING_AUTH_ROUTE_PATHS: readonly string[] = [
  AUTH_LINK_PATH,
  AUTH_DOOR_PATH,
  '/callback',
];

/**
 * Single normalized predicate for "is this an auth-flow route?". Shared by
 * `shouldStoreRedirectPath` here and by the auto-guard in useAuthNavigation.ts,
 * which used to keep its own exact-string `AUTH_PAGES` set.
 */
export const isAuthRoute = (pathname: string): boolean =>
  AUTH_ROUTE_PATHS.has(normalizeRoutePath(pathname));

/**
 * Determines if a path should be stored for post-auth redirects
 */
export const shouldStoreRedirectPath = (path: string): boolean => {
  // AIDEV-NOTE: /home added since it's now the guest-friendly landing page.
  // These are this function's OWN extra exclusions, on top of isAuthRoute():
  // guest-friendly landing pages nobody should be returned to post-auth.
  const nonDestinationPaths = ['/hi', '/home', '/'];
  // AIDEV-NOTE: Exclude guest-browsing routes from redirect storage
  // Event details (/events/:id) ARE stored → user returns to complete check-in
  // Events list and big screen are NOT stored → no meaningful action to complete
  const publicRoutePatterns = [
    /^\/events$/, // Events list - no action to complete
    /^\/events\/[^/]+\/big-screen$/, // Big screen - display only
    // Welcome interstitials are entry funnels, never destinations (design §6):
    // returning a fresh signup to /welcome would replay the marketing carousel.
    /^\/communities\/[^/]+\/welcome/,
  ];

  if (!path) {
    return false;
  }

  // Normalize before comparing: callers normally pass a bare pathname
  // (location.pathname), but a caller that hands over pathname+search (e.g. a
  // spent-link arrival at '/auth?from=expired'), or a trailing-slash/mixed-case
  // variant that React Router matched to the same route, must not slip past
  // these checks just because the raw string doesn't match exactly.
  const pathname = normalizeRoutePath(path);

  if (isAuthRoute(pathname) || nonDestinationPaths.includes(pathname)) {
    return false;
  }

  return !publicRoutePatterns.some(pattern => pattern.test(pathname));
};

/**
 * The ONE writer of the redirect stash: the path and its freshness stamp, which
 * must always move together (a path with a stale stamp is invisible to
 * `readSessionRedirectPath`, a stamp with no path is a lie). Returns whether the
 * write landed.
 *
 * Private on purpose. The three public entry points differ only in how they
 * VALIDATE their input -- `storeRedirectPath` composes the value from this
 * document, `storeCombinedRedirectPath` takes a server-round-tripped one and
 * gates it hard, `hydrateRedirectPathFromMagicLinkMirror` takes one this origin
 * wrote a moment ago -- and each of them used to carry its own copy of the same
 * two setItem calls inside its own try/catch. `source` is which of the three, so
 * the one warning below stays as attributable as the three it replaced.
 */
const writeRedirectStash = (value: string, source = 'storage'): boolean => {
  try {
    globalThis.sessionStorage.setItem(REDIRECT_PATH_KEY, value);
    globalThis.sessionStorage.setItem(REDIRECT_TIMESTAMP_KEY, Date.now().toString());
  } catch (e) {
    // AIDEV-NOTE [MEETBALL magic-link]: the `source` label keeps this line
    // ATTRIBUTABLE. Extracting the three writers' duplicated setItem pair into
    // this one function collapsed their distinct warnings into a single string,
    // and the three failures are not the same incident: an ordinary
    // `storeRedirectPath` miss costs one post-auth destination, while a failed
    // MIRROR HYDRATION is a cross-context magic-link handover that arrived and
    // was then dropped on the floor -- the exact silent-loss class this feature
    // exists to close, and the one worth being able to grep for on its own.
    logger.warn(`sessionStorage unavailable for redirect path ${source}`, e);
    return false;
  }
  return true;
};

/**
 * Stores the intended path (with search/hash) for redirecting after auth.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: it gates the COMBINED value through the same
 * predicate the other two writers use, and it used to gate only `path`. That left
 * three writers of one stash with three different rule sets -- this one checked
 * the pathname and appended `location.search` + `location.hash` unchecked, the
 * mirror hydrator wrote raw behind a bare `typeof value === 'string'`, and only
 * `storeCombinedRedirectPath` applied the real rules -- so what
 * `useCallbackRedirect` eventually navigates to depended on which door it came in
 * through. One predicate, three callers.
 *
 * What the local writer gives up by joining them: a destination whose own query
 * string contains a literal `://` or a backslash is now refused (nothing this app
 * produces, and a visitor-typed one degrades to "no stored destination", never to
 * a wrong one). Auth routes were already excluded and control characters do not
 * occur in a live `location`, so those two cost nothing at all.
 */
export const storeRedirectPath = (path: string, search?: string, hash?: string): void => {
  const resolvedSearch = search ?? globalThis.location.search;
  const resolvedHash = hash ?? globalThis.location.hash;

  const combined = `${path}${resolvedSearch}${resolvedHash}`;
  if (!isStorableCombinedRedirectPath(combined)) {
    return;
  }

  writeRedirectStash(combined);
};

/**
 * Longest value accepted as a redirect path. Matches the backend's own cap on
 * the field, and keeps a hostile value from filling sessionStorage.
 */
const MAX_REDIRECT_PATH_LENGTH = 2048;

/**
 * The code points the BACKEND's `redirect_path` validator rejects, character for
 * character: `_CONTROL_CHARACTERS` in `app/schemas/magic_link.py`.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: this class is the one thing the two ends of
 * the carrier have to agree on, and it was three code points short. U+0085 NEL,
 * U+2028 LINE SEPARATOR and U+2029 PARAGRAPH SEPARATOR are exactly the ones a
 * byte-oriented check misses: JSON carries them literally, and a JS engine reads
 * U+2028/U+2029 as LINE TERMINATORS, so they split a log line that a human or a
 * parser then reads as two. A path the backend refuses to accept must not be one
 * this client is willing to store on the way back; that asymmetry could only
 * ever be discovered by whatever the extra characters went on to break.
 */
// eslint-disable-next-line no-control-regex -- matching control characters IS the point
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F\u0085\u2028\u2029]/u;

/**
 * Whether an already-combined `pathname + search + hash` string that did NOT
 * come from this document is safe to use as the post-auth destination. Decides
 * only: it writes nothing and reads nothing outside its argument.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: this is the gate `storeRedirectPath` never
 * needed. The magic-link flow context is round-tripped through the server, so
 * the string arriving here was authored in some other browsing context and
 * reaches this tab over the network -- it has to pass the same checks a locally
 * captured path passes, and two more besides.
 *
 * COMPOSED from `isSafeInAppPath`, and that is the fix for a gate that was
 * WEAKER than the canonical one while reading as though it were stricter. It
 * used to hand-roll "relative to this origin" as three `startsWith` tests, which
 * catch a backslash or a scheme in the FIRST position and nowhere else:
 * `/events/evt-1?next=/\evil.example` and `/x?u=https://evil.example` both
 * walked through, while `@utils/safePath` -- the predicate the three
 * notification-tap surfaces already share, which rejects `\` and `://` ANYWHERE
 * plus tab/newline/CR -- sat one import away. A second hand-rolled copy of "is
 * this a safe in-app path" is the exact drift that module exists to kill, and
 * this copy is the one standing in front of a value that arrived over the
 * network.
 *
 * The two checks on top of it are the LENGTH CAP and the CONTROL-CHARACTER class
 * above, both taken from the backend's own validator so that a path it refuses to
 * store is not one this client stores on the way back. The reverse does NOT hold
 * and is not claimed: this predicate is strictly the stricter of the two (the
 * backend allows `://` and `\` past position 0, and applies no auth-route or
 * non-destination exclusions at all), so there are paths the backend accepts that
 * are dropped here. That direction is safe -- a dropped destination costs one
 * post-auth landing -- and it is the direction a client-side gate should err in.
 *
 * `shouldStoreRedirectPath` closes it with the auth-route and non-destination
 * exclusions every locally stored path passes; it normalizes off the query and
 * hash itself. `isAuthRoute` is deliberately NOT a conjunct of its own here --
 * it is literally the first test inside that function, so a second copy would
 * only suggest the two are independent.
 *
 * SEPARATE from the writer below on purpose. `/auth/link` has to know whether a
 * delivered destination is USABLE before the visitor has consented to it: the
 * gate names what continuing does, so it is built from the half of the delivery
 * that would survive the write, and the only way to ask used to be to perform
 * the write. See `useMagicLinkFlowContext`.
 */
export const isStorableCombinedRedirectPath = (value: unknown): value is string =>
  // A type guard, so it does the `typeof` work too and no string check of this
  // function's own survives here.
  isSafeInAppPath(value) &&
  value.length <= MAX_REDIRECT_PATH_LENGTH &&
  !CONTROL_CHARACTERS.test(value) &&
  shouldStoreRedirectPath(value);

/**
 * Stores an already-combined `pathname + search + hash` string as the post-auth
 * destination. Returns whether it was stored.
 *
 * The rules live in `isStorableCombinedRedirectPath` above and there is only the
 * one copy of them: a caller that has already asked gets the same answer here,
 * so re-checking at the write costs nothing, and a caller that never asked
 * cannot get past the gate by not asking.
 *
 * `storeRedirectPath` cannot do this job: it derives `search`/`hash` from the
 * CURRENT location when they are not passed (which here is `/auth/link`), so a
 * combined string would come back mangled -- the same reason
 * `hydrateRedirectPathFromMagicLinkMirror` writes the keys raw.
 */
export const storeCombinedRedirectPath = (value: string): boolean =>
  isStorableCombinedRedirectPath(value) && writeRedirectStash(value, 'delivery');

// =============================================================================
// CROSS-TAB MAGIC-LINK MIRROR
// =============================================================================

/**
 * AIDEV-NOTE [MEETBALL magic-link]: sessionStorage is per-tab, and a magic link
 * is opened in a NEW tab -- frequently in another browser or on another device.
 * By the time the callback lands, the sessionStorage stash written before the
 * email was sent is simply not there, so every auth-gated CTA silently degraded
 * to a plain /home landing. The redirect path is therefore MIRRORED into
 * localStorage, but only when the /auth door flow is actually entered (see the
 * `routeToDoor` branches in useSignInOut.ts and useAuthNavigation.ts) -- never
 * on an ordinary sign-in, which has no cross-tab hop to survive.
 *
 * TTL is 30 minutes, matching the 1800s life of the tokens the backend mints for
 * the link. The sessionStorage copy shares it -- see @utils/authFlowTtl.
 *
 * IT IS WRITTEN AT THE DOOR AND READ AT `/auth/link`, and nowhere else. The read
 * is `hydrateRedirectPathFromMagicLinkMirror` below, not a fallback inside
 * `getStoredRedirectPath`: a fallback there applied to every sign-in in the app,
 * including the two that deliberately never pass through the door, so an
 * abandoned envelope was delivered to the next person on the browser profile.
 *
 * Multi-tab caveat: the key is not per-link, so a second door entry overwrites
 * the first -- last writer wins. Accepted for this flow: a visitor driving two
 * concurrent sign-ins in one browser profile is vanishingly rare, and the
 * failure mode is landing on the other tab's destination, not a broken auth.
 */
export const MAGIC_LINK_REDIRECT_STORAGE_KEY = MAGIC_LINK_MIRROR_KEYS.redirectPath;

/** The per-tab copy's TTL, shared with the mirror and the intent. See @utils/authFlowTtl. */
const SESSION_REDIRECT_TTL_MS = AUTH_FLOW_TTL_MS;

/**
 * The mirror's own read guard, and it is the FULL predicate rather than a
 * `typeof`.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: it was `typeof value === 'string'`, on the
 * reasoning that localStorage is same-origin so the only writer is us, from a path
 * `storeRedirectPath` had already gated. Two things were wrong with resting on
 * that. The gate it had "already passed" was the weaker one (see
 * `storeRedirectPath` above -- it checked the pathname and appended search and
 * hash unchecked), and same-origin is not the same as same-BUILD: this value can
 * be up to 30 minutes old, written by a previous version of the app or edited by
 * anyone with devtools open on this profile. Running the one predicate here costs
 * a few string tests on a path that is about to be navigated to.
 */
const isMirroredPath = isStorableCombinedRedirectPath;

/**
 * The per-tab sessionStorage copy, WITH its TTL applied. Both
 * `getStoredRedirectPath` and the mirror go through this: the mirror used to
 * read the raw key and stamp a fresh `writtenAt`, which resurrected a path this
 * TTL had already retired into a fresh 30-minute envelope.
 */
const readSessionRedirectPath = (): string | null => readSessionRedirectPathOrUnreadable() ?? null;

/**
 * `readSessionRedirectPath`, except that a storage read that THROWS answers `undefined`
 * rather than the `null` of a tab holding no destination: one read answers both, for the
 * reason `readAuthIntentOrUnreadable` gives. Every other caller goes through
 * `readSessionRedirectPath`, which is this `?? null`.
 */
const readSessionRedirectPathOrUnreadable = (): string | null | undefined => {
  // try/catch like `readAuthIntentOrUnreadable`, and for a sharper reason than
  // symmetry with `storeRedirectPath` 60 lines up. This function's result is the
  // ARGUMENT to `writeMagicLinkPayloadMirror`, so it is evaluated before that
  // writer's own try/catch can help. The caller in useSignInOut sits in the `routeToDoor`
  // block, which has no try/catch of its own, so a throw there means the
  // `navigate(AUTH_DOOR_PATH)` below it never runs and every sign-in CTA in the
  // app becomes a silent no-op. In useAuthNavigation the surrounding catch
  // swallows it and the auto-guard returns, leaving a guest on a protected route
  // that renders nothing and an effect whose deps are all auth state, so it never
  // re-runs: a blank dead end for the life of the document. The environments
  // where sessionStorage throws -- sandboxed iframes without allow-same-origin,
  // hardened in-app mail webviews, storage blocked by policy -- are exactly the
  // ones this feature's traffic arrives through.
  let storedPath: string | null;
  let timestamp: string | null;
  try {
    storedPath = globalThis.sessionStorage.getItem(REDIRECT_PATH_KEY);
    timestamp = globalThis.sessionStorage.getItem(REDIRECT_TIMESTAMP_KEY);
  } catch (e) {
    logger.warn('sessionStorage unavailable for redirect path read', e);
    return undefined;
  }

  if (!storedPath || !timestamp) {
    return null;
  }

  const age = Date.now() - Number.parseInt(timestamp, 10);
  return Number.isFinite(age) && age < SESSION_REDIRECT_TTL_MS ? storedPath : null;
};

/**
 * Copies the current sessionStorage redirect stash into localStorage so it
 * survives the magic link opening in a different tab. Call ONLY when entering
 * the door flow.
 *
 * An ABSENT stash clears the mirror rather than leaving it: entering the door is
 * the point where the mirror is made to describe THIS flow, and a survivor from
 * an abandoned one would send this sign-in to the previous visitor's
 * destination. See @utils/magicLinkMirror.
 */
export const mirrorRedirectPathForMagicLink = (
  sanitize?: (path: string | null) => string | null,
): void => {
  // `sanitize` is how the door mirrors a REDUCED copy for a send to a second
  // address: a destination bearing `?invite=` is a capability, and the mirror is
  // read back by whoever redeems a link in this profile, so it must not re-arm one
  // the upload just refused to carry. The door's mount passes it too once a send from
  // this tab has gone out stripped (the send-elsewhere signal in `firstSendAddress.ts`:
  // a send to an address other than the bound one, or any send from a tab whose storage
  // refused the binding), and never on "Wrong address?" alone, which sends nobody
  // anything; otherwise absent on a door arrival.
  const path = readSessionRedirectPath();
  writeMagicLinkPayloadMirror(
    MAGIC_LINK_MIRROR_KEYS.redirectPath,
    sanitize ? sanitize(path) : path,
  );
};

/**
 * Copies the cross-tab mirror into THIS tab's sessionStorage, and drops the
 * mirror. Called once, by `/auth/link`, at the moment a magic-link redemption
 * is actually starting.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: this replaces a fallback that used to sit
 * inside `getStoredRedirectPath`, where every sign-in in the app read it. That
 * was the wrong scope by a wide margin. Two documented sign-in paths never touch
 * the door -- `useInvalidGrantHandler`'s full-login fallback (which
 * `useSignInOut` says explicitly must never be door-routed) and every native
 * sign-in -- so an abandoned door entry's destination sat in localStorage for 30
 * minutes and was picked up by whoever signed in next on the same browser
 * profile. On a shared laptop that is one person landing on another person's
 * invite URL.
 *
 * The mirror is a COURIER between two tabs, and `/auth/link` is the address on
 * the envelope: it is the only surface in the app that knows the current
 * document is finishing a magic-link flow. Hydrating there and reading
 * sessionStorage everywhere else means the value is scoped to the flow that
 * wrote it without needing a flow id to compare, and `/callback` reads it back
 * out of sessionStorage exactly as it does on an ordinary sign-in -- the same
 * tab survives the hop to Logto and back, which is the mechanism
 * `silentReauthSlot` has relied on for far longer than this feature.
 *
 * Returns whether anything was hydrated, so the caller can log/assert.
 */
export const hydrateRedirectPathFromMagicLinkMirror = (): boolean => {
  const mirrored = readMagicLinkMirror(MAGIC_LINK_MIRROR_KEYS.redirectPath, isMirroredPath);
  clearMagicLinkMirror(MAGIC_LINK_MIRROR_KEYS.redirectPath);

  if (mirrored === null) {
    return false;
  }

  // Written raw rather than through `storeRedirectPath`, which would re-derive
  // search and hash from the CURRENT location (which is /auth/link) and mangle a
  // value that is already a full `path + search + hash` string. The RULES are the
  // same ones either way: `isMirroredPath` above is `isStorableCombinedRedirectPath`,
  // so the mirror's value has passed the identical gate before reaching this line.
  return writeRedirectStash(mirrored, 'hydration');
};

/**
 * Retrieves the stored redirect path if still valid.
 *
 * sessionStorage ONLY. The cross-tab mirror reaches this value through
 * `hydrateRedirectPathFromMagicLinkMirror`, which `/auth/link` calls on the one
 * document that is provably finishing a magic-link flow. See that function for
 * why a fallback here was too wide.
 *
 * @returns The stored redirect path, or `null` if no valid stored path exists.
 */
export const getStoredRedirectPath = (): string | null => {
  if (typeof globalThis === 'undefined') {
    return '/home';
  }

  return readSessionRedirectPath();
};

/**
 * `getStoredRedirectPath`, except that a storage read that THROWS answers `undefined`
 * rather than `null`. For the fail-closed strip only (`holdStashToRule` in
 * `@lib/magicLink/capabilityStash`; F2 of #1253's adversarial review), which clears a
 * destination it could not read, from this one read, for the reason
 * `readAuthIntentOrUnreadable` gives.
 */
export const getStoredRedirectPathOrUnreadable = readSessionRedirectPathOrUnreadable;

/**
 * Clears THIS TAB's redirect stash and leaves the cross-tab mirror exactly as it
 * is. For the door's fail-closed strip (`holdStashToRule` in
 * `@lib/magicLink/capabilityStash`), for the reason `clearAuthIntentKeepingMirror`
 * gives: the envelope describes the link a send just minted, and only the door's
 * mirror writers decide what it says.
 * `clearRedirectPath` below is this plus the mirror.
 */
export const clearRedirectPathKeepingMirror = (): void => {
  if (typeof globalThis === 'undefined') {
    return;
  }

  // AIDEV-NOTE [MEETBALL magic-link]: guarded for the same reason
  // `readSessionRedirectPath` and `storeRedirectPath` are, and it matters more
  // here than in either of them because of what runs AFTER it.
  // `useCallbackRedirect.performRedirect` calls `clearRedirectPath` first, then
  // clears the auth intent, writes the celebration marker and navigates. A throw on the
  // `sessionStorage` property access -- sandboxed iframes without
  // allow-same-origin, hardened in-app mail webviews, storage blocked by policy,
  // i.e. exactly the environments a magic link is opened in -- took all four
  // down and left the visitor parked on `/callback` under the transit shell with
  // nothing to click. The mirror clear sits outside this try, in `clearRedirectPath`,
  // for the same reason: it must not be collateral damage of the line above it.
  try {
    globalThis.sessionStorage.removeItem(REDIRECT_PATH_KEY);
    globalThis.sessionStorage.removeItem(REDIRECT_TIMESTAMP_KEY);
  } catch (e) {
    logger.warn('sessionStorage unavailable for redirect path clear', e);
  }
};

/**
 * Clears any stored redirect metadata, from BOTH storages: consumers call this
 * on consumption and on a successful non-magic sign-in, and leaving the
 * localStorage mirror behind would bleed one flow's destination into the next.
 */
export const clearRedirectPath = (): void => {
  if (typeof globalThis === 'undefined') {
    return;
  }

  clearRedirectPathKeepingMirror();
  clearMagicLinkMirror(MAGIC_LINK_MIRROR_KEYS.redirectPath);
};

// =============================================================================
// JWT TOKEN UTILITIES
// AIDEV-NOTE: Used for proactive token refresh based on actual expiry time
// =============================================================================

/**
 * Decodes a JWT token and extracts the payload without verification.
 * AIDEV-NOTE: We only need to read the expiry claim, not verify the signature.
 * The backend/Logto handles signature verification.
 *
 * @param token - The JWT token string
 * @returns The decoded payload object, or null if invalid
 */
interface DecodedJwtPayload {
  exp?: number;
  [key: string]: unknown;
}

export const decodeJwtPayload = (token: string): DecodedJwtPayload | null => {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode base64url payload (middle part)
    // Replace URL-safe characters and add padding if needed
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = atob(padded);
    const parsed: unknown = JSON.parse(decoded);
    if (parsed && typeof parsed === 'object') {
      return parsed as DecodedJwtPayload;
    }
    return null;
  } catch (err) {
    logger.debug('Failed to decode JWT payload:', err);
    return null;
  }
};

/**
 * Extracts the expiry time from a JWT token.
 *
 * @param token - The JWT token string
 * @returns The expiry time in milliseconds since epoch, or null if invalid/missing
 */
export const getTokenExpiryMs = (token: string): number | null => {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return null;
  }

  // JWT exp claim is in seconds, convert to milliseconds
  return payload.exp * 1000;
};

/**
 * Calculates milliseconds until a JWT token expires.
 *
 * @param token - The JWT token string
 * @returns Milliseconds until expiry (negative if expired), or null if invalid
 */
export const getMsUntilTokenExpiry = (token: string): number | null => {
  const expiryMs = getTokenExpiryMs(token);
  if (expiryMs === null) {
    return null;
  }

  return expiryMs - Date.now();
};

/**
 * Checks if a JWT token will expire within a given time window.
 *
 * @param token - The JWT token string
 * @param windowMs - Time window in milliseconds (default: 5 minutes)
 * @returns true if token expires within window, false otherwise, null if invalid
 */
export const isTokenExpiringSoon = (
  token: string,
  windowMs: number = 5 * 60 * 1000,
): boolean | null => {
  const msUntilExpiry = getMsUntilTokenExpiry(token);
  if (msUntilExpiry === null) {
    return null;
  }

  return msUntilExpiry < windowMs;
};

/**
 * Session storage key for silent re-auth state.
 *
 * Exported because `index.html`'s inline transit-shell gate reads it directly:
 * that script runs before any module exists, and it is the only thing that can
 * stop the full-screen "Signing you in" shell from painting on a return that the
 * visitor never asked for. Pinned against this constant by
 * `staticTransitShell.test.ts`. That script deliberately only tests that
 * SOMETHING is stored under the key (it has no copy of `decodeEnvelope` or the
 * TTL and mirrors neither), so an expired marker still suppresses the shell on
 * that first paint; the React tree, which does check the TTL, takes over from there.
 */
export const SILENT_REAUTH_KEY = 'meetball_silent_reauth';

/** How long a silent re-auth marker is honoured. Silent auth should be fast. */
const SILENT_REAUTH_TTL_MS = 2 * 60 * 1000;

/**
 * Marks that we're attempting silent re-authentication (`prompt=none`), so the
 * `/callback` that follows can tell the return leg of that attempt from a visible
 * sign-in. Written by `useInvalidGrantHandler`.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: one reader, `useCallbackRedirect`, which returns
 * its answer to `Callback`, so the redirect and the paint cannot disagree at the TTL
 * boundary. Cleared by:
 * - `useCallbackRedirect`'s mount effect, fresh or stale: a stale marker left standing
 *   would make the NEXT callback in this tab read as a silent return;
 * - `useInvalidGrantHandler`, before it escalates to a full login;
 * - the sign-out sweep (`storageCleanup.ts`).
 */
export const silentReauthSlot = defineSessionSlot<true>({
  key: SILENT_REAUTH_KEY,
  ttlMs: SILENT_REAUTH_TTL_MS,
  isValue: isTrue,
});
